import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

/**
 * Error reporting to GlitchTip, which speaks the Sentry protocol.
 *
 * Switched off entirely when no DSN is configured, so a developer machine and
 * a preview build run without one. Vite substitutes the variable at build time,
 * so a build with no DSN leaves the whole SDK out of the bundle: measured at
 * 51 kB against 201 kB with reporting on. Replay, profiling and cron check-ins
 * are deliberately absent; GlitchTip does not implement those endpoints.
 */
export function installErrorReporting(app: App, router: Router): void {
  const dsn = import.meta.env.VITE_GLITCHTIP_DSN
  if (dsn === undefined || dsn === '') return

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.VITE_APP_ENV ?? 'development',
    release: import.meta.env.VITE_APP_VERSION,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.1,
    // The bearer token is in localStorage, and a breadcrumb or a URL should
    // never carry it into a third-party service.
    sendDefaultPii: false,
  })
}

/**
 * Sends a failure the shell handled itself but still wants seen, such as a
 * chunk that stayed missing after a reload (#88). A no-op without a DSN.
 */
export function reportError(error: unknown): void {
  Sentry.captureException(error)
}

/** Tags reports with who hit them, so a support ticket resolves to a session. */
export function identify(user: { id: string; email: string } | null): void {
  Sentry.setUser(user === null ? null : { id: user.id, email: user.email })
}

/**
 * Pins the API's request id to the next report, so a toast's reference and a
 * GlitchTip event point at the same server-side log line.
 */
export function noteRequestId(requestId: string): void {
  Sentry.setTag('request_id', requestId)
  Sentry.addBreadcrumb({ category: 'api', message: `request ${requestId} failed`, level: 'error' })
}
